<template>
  <div class="card main-info">
    <div class="card-body">
      <LineChart class="mt-2" :finances="chartData" />
      <ul class="info-list mt-3">
        <li><span class="info-title">Финансовое состояние</span></li>
        <li
          v-for="factor in factors"
          :key="factor.id"
          class="flex flex-row justify-content-between align-items-center border-bottom mt-2"
        >
          <span class="info-value success w-auto">{{ factor.title }}</span
          ><span class="info-bold">{{ factor.text }}</span>
          <span class="info-percent" :class="getPercentClass(factor.percent)">
            {{ factor.percent }}
          </span>
        </li>
      </ul>
      <button
        class="btn-primary btn-small d-block ms-auto mt-2"
        @click="emitChangeTab('tab-finances')"
      >
        Подробнее
      </button>
    </div>
  </div>
</template>

<script>
import LineChart from "./Diagrams/LineChart.vue";
import { defineComponent, ref, onMounted, watch } from "vue";

// helper: из html_checko -> { finances_years: [{year, amount:[number]}] }
function parseHtmlToFinances(html, code = "2110") {
  if (!html) return { finances_years: [] };

  // 1) labels
  const mLabels = html.match(
    /\b(?:var|let|const)\s+labels\s*=\s*\[([\s\S]*?)\]\s*;?/m
  );
  const labels = (mLabels?.[1] || "")
    .split(",")
    .map((s) => s.trim().replace(/^['"]|['"]$/g, ""))
    .filter(Boolean);

  // 2) series для нужного code (например '2110' — выручка, '2400' — прибыль)
  const esc = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const codeEsc = esc(code);

  const blockRe = new RegExp(
    [
      "\\bif\\s*\\(\\s*code\\s*(?:==|===)\\s*['\"]",
      codeEsc,
      "['\"]\\s*\\)\\s*",
      "(?:\\{[\\s\\S]*?)?",
      "\\b(?:var\\s+|let\\s+|const\\s+)?series\\s*=\\s*\\[\\[([\\s\\S]*?)\\]\\]\\s*;?",
    ].join(""),
    "m"
  );
  const block = html.match(blockRe)?.[1] || "";

  // 3) вытянуть value
  const itemRe = /\bvalue\s*:\s*(['"])(.*?)\1/g;
  const values = [];
  let p;
  while ((p = itemRe.exec(block)) !== null) {
    const raw = p[2] || "0";
    const num = Number(String(raw).replace(/\s/g, "")) || 0;
    values.push(num);
  }

  // 4) собрать под формат LineChart: amount — массив (твой код суммирует amount[])
  const years = labels.map((year, i) => ({
    year,
    amount: [values[i] ?? 0],
  }));

  return { finances_years: years };
}

export default defineComponent({
  name: "FinancesInfo",
  components: { LineChart },
  props: {
    finances: Object,
    html: String,
  },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setup(props, { emit }) {
    const factors = ref([]);
    const chartData = ref({ labels: [], datasets: [] });

    const emitChangeTab = (newTab) => {
      emit("change-tab", newTab);
    };

    const getPercentClass = (percent) => {
      if (!percent) return "";
      if (percent.includes("+")) return "is-positive";
      if (percent.includes("-")) return "is-negative";
      return "";
    };

    const getParsing = () => {
      if (!props.html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(props.html, "text/html");
      const finances = doc.getElementById("finances");
      const info = finances?.querySelector("#accounting-huge");
      const table = finances?.querySelector("table");

      if (info) {
        const rows = info?.querySelectorAll(".col-12");
        if (rows?.length) {
          rows?.forEach((stroke, index) => {
            const rowTitle = stroke.querySelector("em");
            const rowValue = stroke.querySelector(".text-huge a");
            const rowPercent = stroke.querySelector(".text-huge small");
            if (
              rowTitle?.textContent &&
              rowTitle.textContent.trim() !== "Активы"
            ) {
              const tableItemObject = {
                id: index + 1,
                title: rowTitle.textContent.trim() || null,
                text: rowValue?.textContent?.trim() || null,
                percent: rowPercent?.textContent?.trim() || "",
              };
              factors.value.push(tableItemObject);
            }
          });
        }
      } else {
        if (table) {
          const tr = table?.querySelectorAll("tr");
          if (tr?.length) {
            tr?.forEach((stroke, index) => {
              const tdFirst = stroke.querySelector("td:nth-child(1)");
              const tdSecond = stroke.querySelector("td:nth-child(2) span");
              const tdPercent = stroke.querySelector("td:nth-child(2) small");
              if (
                tdFirst?.textContent &&
                tdFirst.textContent.trim() !== "Активы"
              ) {
                const tableItemObject = {
                  id: index + 1,
                  title: tdFirst.textContent.trim() || null,
                  text: tdSecond?.textContent?.trim() || null,
                  percent: tdPercent?.textContent?.trim() || "",
                };
                factors.value.push(tableItemObject);
              }
            });
          }
        }
      }
    };

    const hydrate = () => {
      if (!props.html) return;
      chartData.value = parseHtmlToFinances(props.html);
      factors.value = [];
      getParsing();
    };

    onMounted(hydrate);
    watch(() => props.html, hydrate);
    return {
      emitChangeTab,
      factors,
      chartData,
      getPercentClass,
    };
  },
});
</script>
